import React, { useState, useRef } from "react";
import { useIntl } from "gatsby-plugin-intl";
import { useStaticQuery, graphql } from "gatsby"
import BlockContent from "@sanity/block-content-to-react"; // https://github.com/sanity-io/block-content-to-react
import { getGatsbyImageData } from 'gatsby-source-sanity'
import { GatsbyImage } from 'gatsby-plugin-image'
import addToMailchimp from 'gatsby-plugin-mailchimp'
import { Swiper, SwiperSlide } from "swiper/react"
import SwiperCore, { Mousewheel } from "swiper";

import TeaserCase from "./teaserCase";
import TeaserCaseSmall from "./teaserCaseSmall";
import TeaserInsight from "./teaserInsight";
import TeaserExclusive from "./teaserExclusive";
import Callout from "./callout";
import AudioBlock from "./audioBlock";
import AudioGalleryBlock from "./audioGalleryBlock";

import ArrowRightShort from "../assets/images/svg/arrow-right-short.svg";

SwiperCore.use([Mousewheel]);

let allImagesReferences = []
let allFilesReferences = []
let allArticlesReferences = []
let useBlackTheme = false

const linkMarkSerializer = (props) => {
  const href = props.mark.href;
  // let target = href.toLowerCase().includes('://tro.de') || href.toLowerCase().includes('://www.tro.de') ? '_self' : '_blank'
  let target = '_self'
  if (props.mark.hasOwnProperty('blank')) {
    if (props.mark.blank === true) {
      target = '_blank'
    } else {
      target = '_self'
    }
  } else {
    target = href.toLowerCase().includes('://tro.de') || href.toLowerCase().includes('://www.tro.de') ? '_self' : '_blank'
  }
  return <a href={href} target={target}>{props.children[0]}</a>
}

const reformatTextBlock = function (input) {
  let output = []
  if (input) {
    input.split('\n\n').map((el, index) => {
      let content = el.replace('\n', '<br>')
      output.push(<p className="mb-6" key={index} dangerouslySetInnerHTML={{ __html: content }}></p>)
    })
  }
  return output
}

const serializersSimple = {
  container: ({ children }) => {
    return <div className={`tro-body-block-content__simple-richtext`}>{children}</div>;
  },
  marks: {
    link: linkMarkSerializer
  },
  types: {}
}

const swiperSettings = {
  mousewheel: {
    forceToAxis: true,
  },
  spaceBetween: 30,
  slidesOffsetBefore: 22,
  slidesPerView: 1.5,

  breakpoints: {
    450: {
      slidesPerView: 1.5,
    },
    640: {
      slidesPerView: 2,
    },
    768: {
      slidesPerView: 2.4,
      slidesOffsetBefore: 44,
    },
    1000: {
      slidesPerView: 1.7,
      slidesOffsetBefore: 327,
    },
    1200: {
      slidesPerView: 2.2,
      slidesOffsetBefore: 413,
    },
    2000: {
      slidesPerView: 3.1,
      slidesOffsetBefore: 520,
    },
    2100: {
      slidesPerView: 3.7,
      slidesOffsetBefore: 623,
    },
  }
}

const calculateOffset = (swiper) => {
  let slidesOffsetBefore = 0;
  const windowWidth = window.innerWidth;
  if (windowWidth < 767) {
    slidesOffsetBefore = 22 // 1rem padding left
  } else if (windowWidth >= 768 && windowWidth <= 999) {
    slidesOffsetBefore = 44 // 2rem padding left

  } else {
    slidesOffsetBefore = (windowWidth * 0.244) + 44
  }
  swiper.params.slidesOffsetBefore = slidesOffsetBefore;
  swiper.update()
}

const serializers = {
  container: ({ children }) => {
    return <div className={`tro-body-block-content ${useBlackTheme ? 'tro-body-block-content--dark' : ''}`}>{children}</div>;
  },
  marks: {
    link: linkMarkSerializer
  },
  types: {
    imageBlock: (props) => {
      const imageData = getGatsbyImageData(props.node.image.asset._ref, { maxWidth: 1400 }, { projectId: process.env.GATSBY_SANITY_PROJECT_ID, dataset: process.env.GATSBY_SANITY_DATASET })
      const fullwidth = props.node.fullwidth
      if (props.node.url) {
        return (
          <figure className={`tro-body-block-content__image ${fullwidth ? 'tro-body-block-content__image--fullwidth' : ''}`}>
            <a href={props.node.url} target="_blank">
              <GatsbyImage image={imageData} alt={props.node.alt || ""} />
            </a>
            {props.node.caption && (
              <figcaption>
                {props.node.caption}
              </figcaption>
            )}
          </figure>
        )
      } else {
        return (
          <figure className={`tro-body-block-content__image ${fullwidth ? 'tro-body-block-content__image--fullwidth' : ''}`}>
            <GatsbyImage image={imageData} alt={props.node.alt || ""} />
            {props.node.caption && (
              <figcaption>
                {props.node.caption}
              </figcaption>
            )}
          </figure>
        )
      }
    },
    audioBlock: (props) => {
      if (props.node.audiofile && props.node.audiofile.asset) {
        const asset = props.node.audiofile.asset || null
        if (asset) {
          const assetUrl = allFilesReferences.find(el => el.id === asset._ref).url
          return (
            <AudioBlock title={props.node.title || ''} assetUrl={assetUrl} standalone />
          )
        } else {
          return null
        }
      } else {
        return null
      }
    },
    audioGalleryBlock: (props) => {
      const assets = props.node.audiofiles?.map((file) => {
        return {
          title: file.title,
          duration: file.duration,
          assetUrl: allFilesReferences.find(el => el.id === file.audiofile.asset._ref).url
        }
      })

      return <AudioGalleryBlock assets={assets} />
    },
    videoBlock: (props) => {
      const poster = props.node.videoPoster ? allImagesReferences.find(el => el.id === props.node.videoPoster.asset._ref)?.gatsbyImageData.images.fallback.src : '';
      const intl = useIntl()
      return (
        <div className="mb-8 col-start-1 col-span-full tablet:col-start-3 tablet:col-span-8 medium:mb-16">
          {props.node.videotitle && props.node.videotitle[intl.locale] && (
            <h5 className="mb-2">{props.node.videotitle[intl.locale]}</h5>
          )}
          {props.node.video && (
            <video controls={1} preload="auto" poster={poster}>
              <source src={poster ? props.node.video : `${props.node.video}#t=0.1`} type="video/mp4"></source>
            </video>
          )}
        </div>
      )
    },
    youtubeBlock: (props) => {
      const intl = useIntl()
      return (
        <div className="mb-8 tro-body-block-content__youtube col-start-1 col-span-full tablet:col-start-3 tablet:col-span-8 medium:mb-16">
          {props.node.videotitle && props.node.videotitle[intl.locale] && (
            <h5 className="mb-2">{props.node.videotitle[intl.locale]}</h5>
          )}
          <iframe
            src={`https://www.youtube.com/embed/${props.node.youtubeid}?rel=0`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
        </div>
      )
    },
    quoteBlock: (props) => {
      return (
        <blockquote>
          {props.node.quote}
          {props.node.author && (
            <small>– {props.node.author}</small>
          )}
        </blockquote>
      );
    },
    imageTextBlock: (props) => {
      const imageData = getGatsbyImageData(props.node.image.asset._ref, { maxWidth: 1400 }, { projectId: process.env.GATSBY_SANITY_PROJECT_ID, dataset: process.env.GATSBY_SANITY_DATASET })
      return (
        <div className="col-span-full tro-grid-responsive">
          <div className="col-span-full medium:col-span-5">
            <figure className="tro-body-block-content__image">
              <GatsbyImage image={imageData} alt={props.node.alt || ""} />
              {props.node.caption && (
                <figcaption>
                  {props.node.caption}
                </figcaption>
              )}
            </figure>
          </div>

          <div className="col-span-full medium:col-span-6">
            {props.node.simpleRichText && (
              <BlockContent blocks={props.node.simpleRichText} serializers={serializersSimple} renderContainerOnSingleChild={true} />
            )}
            {!props.node.simpleRichText && (
              reformatTextBlock(props.node.copy)
            )}
          </div>
        </div>
      )
    },
    textImageBlock: (props) => {
      const imageData = getGatsbyImageData(props.node.image.asset._ref, { maxWidth: 1400 }, { projectId: process.env.GATSBY_SANITY_PROJECT_ID, dataset: process.env.GATSBY_SANITY_DATASET })
      return (
        <div className="col-span-full tro-grid-responsive">
          <div className="col-span-full medium:col-start-2 medium:col-span-6">
            {props.node.simpleRichText && (
              <BlockContent blocks={props.node.simpleRichText} serializers={serializersSimple} renderContainerOnSingleChild={true} />
            )}
            {!props.node.simpleRichText && (
              reformatTextBlock(props.node.copy)
            )}
          </div>
          <div className="col-span-full medium:col-span-5">
            <figure className="tro-body-block-content__image">
              <GatsbyImage image={imageData} alt={props.node.alt || ""} />
              {props.node.caption && (
                <figcaption>
                  {props.node.caption}
                </figcaption>
              )}
            </figure>
          </div>
        </div>
      )
    },
    videoTextBlock: (props) => {
      const poster = props.node.videoPoster ? allImagesReferences.find(el => el.id === props.node.videoPoster.asset._ref)?.gatsbyImageData.images.fallback.src : '';
      return (
        <div className="col-span-full tro-grid-responsive">
          <div className="col-span-full medium:col-span-5">
            {props.node.hideControls && (
              <video autoPlay={true} loop={true} muted={true} playsInline={true} className="w-full" poster={poster}>
                <source src={poster ? props.node.video : `${props.node.video}#t=0.1`} type="video/mp4" />
              </video>
            )}
            {!props.node.hideControls && (
              <video controls={1} preload="auto" className="w-full" poster={poster}>
                <source src={poster ? props.node.video : `${props.node.video}#t=0.1`} type="video/mp4" />
              </video>
            )}
          </div>
          <div className="col-span-full medium:col-span-6">
            {props.node.simpleRichText && (
              <BlockContent blocks={props.node.simpleRichText} serializers={serializersSimple} renderContainerOnSingleChild={true} />
            )}
            {!props.node.simpleRichText && (
              reformatTextBlock(props.node.copy)
            )}
          </div>
        </div>
      )
    },
    textVideoBlock: (props) => {
      const poster = props.node.videoPoster ? allImagesReferences.find(el => el.id === props.node.videoPoster.asset._ref)?.gatsbyImageData.images.fallback.src : '';
      return (
        <div className="col-span-full tro-grid-responsive">
          <div className="col-span-full medium:col-start-2 medium:col-span-6">
            {props.node.simpleRichText && (
              <BlockContent blocks={props.node.simpleRichText} serializers={serializersSimple} renderContainerOnSingleChild={true} />
            )}
            {!props.node.simpleRichText && (
              reformatTextBlock(props.node.copy)
            )}
          </div>
          <div className="col-span-full medium:col-span-5">
            {props.node.hideControls && (
              <video autoPlay={true} loop={true} muted={true} playsInline={true} className="w-full" poster={poster}>
                <source src={poster ? props.node.video : `${props.node.video}#t=0.1`} type="video/mp4" />
              </video>
            )}
            {!props.node.hideControls && (
              <video controls={1} preload="auto" className="w-full" poster={poster}>
                <source src={poster ? props.node.video : `${props.node.video}#t=0.1`} type="video/mp4" />
              </video>
            )}
          </div>
        </div>
      )
    },
    calloutBlock: (props) => {
      if (props.node.headline || props.node.body) {
        return (
          <div className="col-span-full">
            <Callout headline={props.node.headline || null} subline={props.node.body || null} />
          </div>
        )
      } else {
        return (<div></div>)
      }
    },
    spacerBlock: (props) => {
      const divider = props.node.divider;
      return (
        <div className={`tro-body-block-content__spacer ${divider ? 'tro-body-block-content__spacer--divider' : ''}`} />
      )
    },
    caseSelect: (props) => {
      const article = allArticlesReferences.find(el => el._id === props.node.case._ref || el.id === props.node.case._ref)
      return (
        <article>
          <TeaserCase
            item={article}
            isBanner={true}
            bannerProperties={{
              color: "bg-brand-light",
              isTextRight: false,
            }}
            openInNewTab={props.node.openInNewTab}
          />
        </article>
      )
    },
    insightSelect: (props) => {
      const article = allArticlesReferences.find(el => el._id === props.node.insight._ref || el.id === props.node.insight._ref)
      return (
        <article>
          <TeaserInsight
            item={article}
            customStyle="left"
            openInNewTab={props.node.openInNewTab}
          />
        </article>
      )
    },
    newsletterBlock: (props) => {
      const intl = useIntl()
      const [showForm, setShowForm] = useState(true);
      const [msg, setMsg] = useState('');
      const title = props.node.title
      const subtitle = props.node.subtitle
      const submitNL = function (e) {
        e.preventDefault()
        const email = e.target.elements[0].value
        addToMailchimp(email, {}, props.node.url) // listFields are optional if you are only capturing the email address.
          .then(data => {
            setMsg(data.msg)
            setShowForm(false)
          })
          .catch(err => {
            console.error(err)
          })
      }
      return (
        <div className={`col-span-full ${useBlackTheme ? 'bg-white bg-opacity-10' : 'bg-brand-lighter'} tro-grid-responsive `}>
          <div className="col-start-1 col-span-full tablet:col-start-4 tablet:col-span-6">
            <div className="px-4 py-4 lg:px-0 medium:py-16">
              {title && (
                <h4 className="text-base font-black text-center medium:text-2xl">{title}</h4>
              )}
              {subtitle && (
                <p className="text-center">{subtitle}</p>
              )}
              <div className="mt-6 text-center medium:mt-12">
                {showForm && (
                  <form action="/" method="post" onSubmit={submitNL}>
                    <label className="block pb-0 font-mono text-xs opacity-50 medium:pb-1 text-dark medium:text-sm" htmlFor="nlemailinput"><small>E-Mail</small></label>
                    <div className="flex flex-col">
                      <input className="flex-grow px-4 py-2 text-xs text-center text-black border-gray-400 rounded-full outline-none medium:text-sm border-1 focus:border-black" type="email" name="email" id="nlemailinput" required />
                      <button className="self-center flex-none mt-2 ml-0 text-xs tro-button-cta medium:ml-4 medium:text-sm" type="submit p-2 rounded-lg ">{intl.formatMessage({ id: "submit" })}</button>
                    </div>
                  </form>
                )}

                {!showForm && (
                  <p className="block px-10 py-4 text-black rounded-full bg-brand tro-newsletter-message" dangerouslySetInnerHTML={{ __html: msg }}></p>
                )}

              </div>
            </div>
          </div>
        </div>
      )
    },
    casesSelect: (props) => {
      const swiperRef = useRef(null)
      const [activeIndex, setActiveIndex] = useState(0)
      const articles = props.node.case.map((article) => allArticlesReferences.find(el => el._id === article._ref || el.id === article._ref))

      return (
        <div className="relative tro-body-block-content__case-teasers">
          <button
            className={`absolute z-10 flex items-center justify-center right-4 top-1/2 transform -translate-y-1/2 bg-brand w-7 h-7 -rotate-45 transition-opacity group ${activeIndex === swiperRef.current?.swiper.slides.length - 1 ? 'opacity-0 pointer-events-none' : ''}`}
            onClick={() => swiperRef.current.swiper.slideNext()}
          >
            <ArrowRightShort className="transform rotate-45 group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform duration-300" />
          </button>

          <Swiper 
            {...swiperSettings}
            ref={swiperRef}
            onResize={(swiper) => calculateOffset(swiper)}
            onInit={(swiper) => calculateOffset(swiper)}
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          >
            {articles.map((article, index) => {
              return (
                <SwiperSlide
                  key={index}
                  className={""}
                >
                  <article key={index} style={{ flexShrink: '0' }}>
                    <TeaserCaseSmall
                      item={article}
                      openInNewTab={props.node.openInNewTab}
                    />
                  </article>
                </SwiperSlide>
              )
            })}
          </Swiper>
        </div>
      )
    },
    exclusiveSelect: (props) => {
      const article = allArticlesReferences.find(el => el._id === props.node.exclusive._ref || el.id === props.node.exclusive._ref)

      return (
        <div className="col-span-full">
          <TeaserExclusive
            item={article}
            isBanner={true}
            openInNewTab={props.node.openInNewTab}
          />
        </div>
      )
    }
  }
};

const BodyBlockContent = ({ blackTheme = false, data }) => {
  const dataStatic = useStaticQuery(graphql`
    query bodyBlockData {
      allSanityCase {
        nodes {
          ... Case
        }
      }
      allSanityInsight {
        nodes {
          ... Insight
        }
      }
      allSanityExclusive {
        nodes {
          ... Exclusive
        }
      }
      allSanityFileAsset {
        nodes {
          id
          url
        }
      }
      allSanityImageAsset {
        nodes {
          id
          gatsbyImageData(width: 1400, fit: FILL, formats: JPG)
        }
      }
    }
  `)

  allImagesReferences = dataStatic.allSanityImageAsset && dataStatic.allSanityImageAsset.nodes ? dataStatic.allSanityImageAsset.nodes : []
  allFilesReferences = dataStatic.allSanityFileAsset && dataStatic.allSanityFileAsset.nodes ? dataStatic.allSanityFileAsset.nodes : []
  allArticlesReferences = dataStatic.allSanityCase.nodes.concat(dataStatic.allSanityInsight.nodes, dataStatic.allSanityExclusive.nodes)

  useBlackTheme = blackTheme

  return <BlockContent blocks={data} serializers={serializers} renderContainerOnSingleChild={true} />;
};

export default BodyBlockContent;
